import { Component, OnInit, AfterViewInit, Input, Output, EventEmitter, NgZone, ViewChild } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { MatDialog, MatDialogRef } from '@angular/material';
import { Observable, Subscription } from 'rxjs/Rx';
import * as moment from 'moment';
import { UUID } from 'angular2-uuid';

import { AsperaService } from '../aspera/aspera.service';
import { HistoryService } from './history.service';
import { Transfer } from '../transfers/transfer.model';
import { FormField } from '../forms/form-field.model';
import { FadeInAnimation } from '../shared/fade-in.animation';
import { ListItemAnimation } from '../shared/list-item.animation';
import { TimeOrDatePipe } from '../shared/time-or-date.pipe';
import { TransfersService } from '../transfers/transfers.service';
import { TransferFile } from '../transfers/transfer-file.model';
import { SuccessDialogComponent } from '../shared/success-dialog.component';
import { ErrorDialogComponent } from '../shared/error-dialog.component';
import { ExtendExpirationDialogComponent } from '../transfers/extend-expiration-dialog.component';
import { ShareHistoryComponent } from './share-history.component';
import { HistoryGroupsComponent } from './history-groups.component';
import { User } from '../users/user.model';
import { HistoryGroup } from './history-group.model';
import { AuthService } from '../auth/auth.service';
import { SelectFieldComponent } from '../forms/select-field.component';
import { SearchFieldComponent } from '../forms/search-field.component';
import { DateFieldComponent } from '../forms/date-field.component';

interface ActiveTransfer {
  cookie: string;
  files: TransferFile[];
  transfer: Transfer;
}

@Component({
  selector: 'app-history',
  templateUrl: './history.component.html',
  styleUrls: ['./history.component.scss'],
  animations: [ListItemAnimation(100), FadeInAnimation]
})
export class HistoryComponent implements OnInit, AfterViewInit {
  @ViewChild('endDateField') endDateField: DateFieldComponent;
  @ViewChild('groupField') groupField: SelectFieldComponent;
  @ViewChild('searchField') searchField: SearchFieldComponent;
  @ViewChild('startDateField') startDateField: DateFieldComponent;

  activeTransfers: ActiveTransfer[] = [];
  asperaRunning: boolean = this.asperaService.running;
  count: number = 0;
  currentUser = this.authService.currentUser;
  downloads: Transfer[] = [];
  endDate: string;
  expirationLoading: boolean = false;
  filtersLoading: boolean = true;
  id: string;
  loading: boolean = true;
  page: number;
  search: string;
  selectedTransfer: Transfer;
  groupUsers: User[] = [];
  groupUsersLoaded: boolean = false;
  groupId: number;
  groups: HistoryGroup[] = [];
  groupsLoaded: boolean = false;
  startDate: string;
  subscription: Subscription
  total: number;
  transfers: Transfer[];
  type: string;
  viewLoaded: boolean = false;
  get groupValues() {
    return this.groups.map((g) => {
      return {stored: g.id, displayed: g.name}
    })
  }
  get totalPages(): number {
    return Math.ceil(+this.total/10)
  }

  constructor(
    private asperaService: AsperaService,
    private dialog: MatDialog,
    private ngZone: NgZone,
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService,
    private historyService: HistoryService,
    private transfersService: TransfersService) { }

  ngOnInit() {
    this.route.queryParams.subscribe((params: Params) => {
      this.type = params.t != null ? params.t.toLowerCase() : 'sent';
      this.id = params.id;
      this.page = params.p != null ? +params.p : 1;
      this.search = params.s;
      this.startDate = params.sd != null ? params.sd : moment().subtract(30, 'days').format("YYYY-MM-DD");
      this.endDate = params.ed != null ? params.ed : moment().add(1, 'days').format("YYYY-MM-DD");
      this.groupId = params.g != null ? +params.g : null;

      if (this.viewLoaded) {
        this.endDateField.setValue(this.endDate, false);
        if (this.groupField != null) {
          this.groupField.setValue({stored: this.groupId}, false);
        }
        this.searchField.setValue(this.search, false);
        this.startDateField.setValue(this.startDate, false);
        this.loadHistory();
      }
    });

    this.historyService.groups().subscribe(
      (groups) => {
        this.groups = groups;
        this.groupsLoaded = true;
      }
    )

    this.historyService.groupUsers().subscribe(
      (users) => {
        this.groupUsers = users.filter((u) => u.id !== this.currentUser.id)
        this.groupUsersLoaded = true;
      }
    )

    this.loadAspera();
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.loadHistory();
      this.viewLoaded = true;
    }, 100)
  }

  loadAspera(onSuccess?: () => void, onFailure?: () => void) {
    this.asperaService.startSession(() => {
      this.asperaRunning = this.asperaService.running;
      if (this.asperaRunning) {
        if (onSuccess != null) { onSuccess() }
      } else {
        if (onFailure != null) { onFailure() }
      }
    });
  }

  onFilterChange(event) {
    switch (event.field) {
    case "search":
      let search = event.value != null ? event.value.trim() : null;
      if (this.search != search) {
        this.page = 1;
        this.search = search;
      }
      break;
    case "start_date":
      let startMoment = moment(event.value);
      let startDate = startMoment.isValid() ? startMoment.format("YYYY-MM-DD") : null;
      if (this.startDate != startDate) {
        this.page = 1;
        this.startDate = startDate;
      }
      break;
    case "end_date":
      let endMoment = moment(event.value);
      let endDate = endMoment.isValid() ? endMoment.format("YYYY-MM-DD") : null;
      if (this.endDate != endDate) {
        this.page = 1;
        this.endDate = endDate;
      }
      break;
    case "group":
      let groupId = event.value != null ? +event.value.stored : null;
      if (this.groupId != groupId) {
        this.page = 1;
        this.groupId = groupId;
      }
      break;
    }
    this.updateRoute();
  }

  loadPage(page) {
    this.page = page;
    this.updateRoute();
  }

  changeTab(type) {
    this.type = type
    this.updateRoute();
    this.loadHistory();
  }

  updateRoute() {
    let query: any = {t: this.type};
    if (this.page !== 1) {query.p = this.page};
    if (this.id != null) {query.id = this.id};
    if (this.search != null) {query.s = this.search};
    if (this.startDate != null) {query.sd = this.startDate};
    if (this.endDate != null) {query.ed = this.endDate};
    if (this.groupId != null) {query.g = this.groupId};
    this.router.navigate(["/history"], {queryParams: query})
  }

  loadHistory() {
    this.loading = true;
    this.transfers = [];
    if (this.subscription != null) {this.subscription.unsubscribe()}
    this.subscription = this.historyService.getHistory(
      this.page,
      this.type,
      this.search,
      this.startDate,
      this.endDate,
      null,
      this.groupId).subscribe(
        (data: any) => {
          this.transfers = data.transfers;
          this.count = data.transfers.length;
          this.total = data.total;
          if (this.id != null) {
            this.selectedTransfer = this.transfers.find((t) => t.uploadPackageGuid === this.id);
          }
          this.loading = false;
        },
        (errors) => {
          this.count = 0;
          this.total = 0;
          this.loading = false;
        }
    )
  }

  recipientNames(transfer): any {
    let record = transfer;
    let recipients = transfer.recipients;
    let landingPads = transfer.metadata.Destinations;
    let jellyrollFolders = transfer.metadata.jellyroll;

    if (recipients != undefined && recipients.length > 0) {
      return recipients.map((r) => r.name || r.email).join(", ");
    } else if (landingPads != undefined && landingPads.length > 0) {
      return landingPads.map((d) => d.displayed).join(", ");
    } else if (jellyrollFolders != undefined && jellyrollFolders.length > 0) {
      return jellyrollFolders.map((d) => d.displayed).join(", ");
    } else {
      return "";
    }
  }

  senderName(transfer): string {
    if (transfer.senderFirstName == null || transfer.senderLastName == null) {
      return transfer.senderEmail;
    } else {
      return transfer.senderFirstName + " " + transfer.senderLastName;
    }
  }

  onDownloadFiles(transfer: Transfer, files: TransferFile[]) {
    let download = () => this.downloadFiles(transfer, files).subscribe()

    if (this.asperaRunning) {
      download();
    } else {
      this.loadAspera(
        () => download(),
        () => this.ngZone.run(() => this.showErrorDialog("Aspera Failed to Launch"))
      )
    }
  }

  downloadFiles(transfer: Transfer, files: TransferFile[]): Observable<any> {
    const active = {
      cookie: '',
      files: files.filter((f) => !f.expired),
      transfer: transfer
    };
    return this.transfersService.transferFiles(
      'download',
      active.transfer.uploadPackageGuid,
      active.transfer.uploadPackageId,
      active.files
    ).map(
      (response) => {
        active.cookie = response.cookie;
        this.activeTransfers.push(active);
       },
      (errors) => {
        console.log('Transfer failed: ', errors);
        this.onTransferEnd('failed', active);
      }
    );
  }

  onTransferEnd(status: string, active: ActiveTransfer) {
    let index = this.activeTransfers.indexOf(active);
    if (index > -1) { this.activeTransfers.splice(index,1) }
    if (status === "completed") {
      if (this.activeTransfers.length == 0) {
        this.ngZone.run(() => {this.showSuccessDialog("Files Successfully Downloaded")})
      }
    } else {
      this.ngZone.run(() => {this.showErrorDialog("Failed to Download Files")})
    }
  }

  showSuccessDialog(message: string) {
    let dialogRef = this.dialog.open(SuccessDialogComponent, {
      height: '240px',
      width: '320px',
      data: {message: message}
    });
    setTimeout(() => dialogRef.close(), 3000);
  }

  showErrorDialog(message: string) {
    let dialogRef = this.dialog.open(ErrorDialogComponent, {
      height: '240px',
      width: '320px',
      data: {message: message}
    });
    setTimeout(() => dialogRef.close(), 6000);
  }

  showExtendExpirationDialog(uploadPackageGuid: string, expiration: string) {
    let dialogRef = this.dialog.open(ExtendExpirationDialogComponent, {
      height: '240px',
      width: '320px',
      data: {expiration: expiration}
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result != null && result.expiration != null) {
        this.extendExpiration(uploadPackageGuid, result.expiration);
      }
    })
  }

  extendExpiration(uploadPackageGuid: string, expiration: string) {
    this.expirationLoading = true;
    let transfer = this.transfers.find((t) => t.uploadPackageGuid === uploadPackageGuid);
    let expirationMoment = moment(expiration, 'YYYY-MM-DD')
    if (transfer != null && expirationMoment.isValid()) {
      this.transfersService.extendExpiration(transfer, expiration).subscribe(
        (success) => {
          transfer.expirationDate = expirationMoment;
          this.showSuccessDialog("Expiration Successfully Extended");
          this.expirationLoading = false;
        },
        (error) => {
          this.showErrorDialog("Failed to Extend Expiration");
          this.expirationLoading = false;
        }
      )
    } else {
      this.showErrorDialog("Failed to Extend Expiration");
      this.expirationLoading = false;
    }
  }

  showShareDialog() {
    let dialogRef = this.dialog.open(ShareHistoryComponent, {
      height: '480px',
      width: '480px',
      data: {}
    });
  }

  showGroupsDialog() {
    let dialogRef = this.dialog.open(HistoryGroupsComponent, {
      height: '480px',
      width: '480px',
      data: {
        users: this.groupUsers,
        groups: this.groups
      }
    });
    dialogRef.afterClosed().subscribe(() => {
      this.groups = dialogRef.componentInstance.groups;
      if (this.groupField != null) {
        this.groupField.setValues(this.groupValues);
      }
    })
  }

}



// WEBPACK FOOTER //
// ./src/app/history/history.component.ts